import { useEffect } from "react";

const DEFAULT_DESC =
  "Vind huur- en koopwoningen in heel Nederland. Ontvang woningalerts, ontdek jouw beste matches en vind sneller een woning met WoningAlert.";
const SITE = "WoningAlert";

function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Lightweight per-page SEO: sets document.title, meta description, canonical,
 * robots, Open Graph / Twitter tags, and one or more JSON-LD blocks.
 * Works in a CRA SPA and is captured by the crawler prerender layer.
 *
 * @param exactTitle  when true, `title` is used verbatim (no " · WoningAlert").
 * @param robots      e.g. "index,follow" | "noindex,follow".
 * @param jsonLd      a single object or an array of objects.
 */
export function useSeo({
  title,
  description = DEFAULT_DESC,
  image,
  path,
  jsonLd,
  robots = "index,follow",
  exactTitle = false,
} = {}) {
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";
  useEffect(() => {
    // Accept exactTitle either as `true` (use `title` verbatim) OR as a string
    // (used as the title verbatim and overrides `title`). The second form is
    // the convenient per-page shorthand used across the SPA.
    const exactIsString = typeof exactTitle === "string" && exactTitle.length > 0;
    const resolvedTitle = exactIsString ? exactTitle : title;
    const useExact = exactIsString || exactTitle === true;
    const fullTitle = useExact
      ? resolvedTitle
      : resolvedTitle
      ? `${resolvedTitle} · ${SITE}`
      : `${SITE} | Huur- en koopwoningen in Nederland`;
    document.title = fullTitle;

    setMeta("name", "description", description);
    setMeta("name", "robots", robots);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);

    const origin = process.env.REACT_APP_SITE_URL || window.location.origin;
    const url = `${origin}${path || window.location.pathname}`;
    setMeta("property", "og:url", url);
    if (image) {
      const abs = image.startsWith("http") ? image : `${origin}${image}`;
      setMeta("property", "og:image", abs);
      setMeta("name", "twitter:image", abs);
    }

    // canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // JSON-LD structured data (supports multiple blocks)
    const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    document.querySelectorAll("script[data-wa-jsonld]").forEach((s) => s.remove());
    blocks.forEach((block) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-wa-jsonld", "1");
      script.textContent = JSON.stringify(block);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll("script[data-wa-jsonld]").forEach((s) => s.remove());
      setMeta("name", "robots", "index,follow"); // reset for non-SEO pages
    };
  }, [title, description, image, path, jsonLdKey, robots, exactTitle]);
}
